import { ItemType } from "@/types/enums/item-type";
import { ShopFulfillmentMethod } from "@/types/enums/shop-fulfillment-method";
import {
  PublicShopPromotion,
  ShopCartLine,
  ShopItem,
  ShopShippingPolicy,
} from "@/types/shop";

import { qualifiedGiftItemIds } from "./promotion-messaging";

const safeCents = (value: number | undefined): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

export const unitPriceFor = (line: ShopCartLine): number => {
  switch (line.itemType) {
    case ItemType.GIFT_CARD:
      return safeCents(
        line.kind === "preset" ? line.unitPriceCents : line.amountCents,
      );
    case ItemType.PACKAGE:
    case ItemType.MEMBERSHIP:
    case ItemType.SERVICE:
    case ItemType.PRODUCT:
    case ItemType.MEDICATION:
      return safeCents(line.unitPriceCents);
    default:
      return 0;
  }
};

export const lineSubtotal = (line: ShopCartLine): number =>
  unitPriceFor(line) * line.quantity;

export const shippingSubtotal = (
  lines: ShopCartLine[],
  catalogItems: ShopItem[],
): number => {
  const catalogItemsById = new Map(catalogItems.map((item) => [item.id, item]));
  return lines.reduce((total, line) => {
    if (!("itemId" in line)) return total;
    const catalogItem = catalogItemsById.get(line.itemId);
    return total + (catalogItem?.shipsInOnlineShop ? lineSubtotal(line) : 0);
  }, 0);
};

export const shippingFeeFor = (
  shipping: ShopShippingPolicy | null | undefined,
  shippableSubtotalCents: number,
): number =>
  shipping &&
  shippableSubtotalCents > 0 &&
  (shipping.freeShippingOverCents === null ||
    shippableSubtotalCents <= shipping.freeShippingOverCents)
    ? shipping.feeCents
    : 0;

export interface ShopCartLinePricing {
  originalUnitPriceCents: number;
  unitPriceCents: number;
  originalLineTotalCents: number;
  lineTotalCents: number;
  hasPromotion: boolean;
}

/**
 * Resolve the public/patient-specific catalog price for display without
 * changing the persisted cart price used for server-side stale validation.
 */
export const pricingForCartLine = (
  line: ShopCartLine,
  catalogItems: ShopItem[],
  promotionQuantityAlreadyUsed = 0,
): ShopCartLinePricing => {
  const originalUnitPriceCents = unitPriceFor(line);
  const catalogItem =
    "itemId" in line
      ? catalogItems.find(
          (item) => item.id === line.itemId && item.type === line.itemType,
        )
      : undefined;
  const catalogOriginalPrice = catalogItem
    ? (catalogItem.originalPrice ?? catalogItem.price)
    : null;
  const catalogDisplayPrice = catalogItem?.displayPrice;
  const promotionUnitPriceCents =
    catalogOriginalPrice === originalUnitPriceCents &&
    typeof catalogDisplayPrice === "number" &&
    Number.isFinite(catalogDisplayPrice) &&
    catalogDisplayPrice >= 0 &&
    catalogDisplayPrice < originalUnitPriceCents
      ? catalogDisplayPrice
      : originalUnitPriceCents;
  const promotionQuantity = catalogItem?.bestPromotion?.quantity;
  const discountedQuantity = Math.min(
    line.quantity,
    Math.max(
      0,
      promotionQuantity == null
        ? line.quantity
        : promotionQuantity - promotionQuantityAlreadyUsed,
    ),
  );
  const hasPromotion =
    discountedQuantity > 0 && promotionUnitPriceCents < originalUnitPriceCents;
  const unitPriceCents = hasPromotion
    ? promotionUnitPriceCents
    : originalUnitPriceCents;
  const originalLineTotalCents = originalUnitPriceCents * line.quantity;
  const lineTotalCents =
    originalLineTotalCents -
    (originalUnitPriceCents - promotionUnitPriceCents) * discountedQuantity;

  return {
    originalUnitPriceCents,
    unitPriceCents,
    originalLineTotalCents,
    lineTotalCents,
    hasPromotion,
  };
};

export const pricingForCartLines = (
  lines: ShopCartLine[],
  catalogItems: ShopItem[],
  freeGiftItemIds: ReadonlySet<string> = new Set(),
): Map<string, ShopCartLinePricing> => {
  const promotionQuantityUsed = new Map<string, number>();
  const pricingByLineId = new Map<string, ShopCartLinePricing>();

  for (const line of lines) {
    if ("itemId" in line && freeGiftItemIds.has(line.itemId)) {
      const unitPriceCents = unitPriceFor(line);
      pricingByLineId.set(line.lineId, {
        originalUnitPriceCents: unitPriceCents,
        unitPriceCents: 0,
        originalLineTotalCents: unitPriceCents * line.quantity,
        lineTotalCents: unitPriceCents * (line.quantity - 1),
        hasPromotion: true,
      });
      continue;
    }
    const catalogItem =
      "itemId" in line
        ? catalogItems.find(
            (item) => item.id === line.itemId && item.type === line.itemType,
          )
        : undefined;
    const promotion = catalogItem?.bestPromotion;
    const alreadyUsed = promotion
      ? (promotionQuantityUsed.get(promotion.offerId) ?? 0)
      : 0;
    const pricing = pricingForCartLine(line, catalogItems, alreadyUsed);
    pricingByLineId.set(line.lineId, pricing);

    if (promotion?.quantity != null && pricing.hasPromotion) {
      promotionQuantityUsed.set(
        promotion.offerId,
        alreadyUsed + Math.min(line.quantity, promotion.quantity - alreadyUsed),
      );
    }
  }

  return pricingByLineId;
};

export interface ShopCartPricingPreview {
  pricingByLineId: Map<string, ShopCartLinePricing>;
  merchandiseSubtotalCents: number;
  displaySubtotalCents: number;
  shippingFeeCents: number;
  totalCents: number;
}

export const getShopCartPricingPreview = (
  lines: ShopCartLine[],
  catalogItems: ShopItem[],
  shipping: ShopShippingPolicy | null | undefined,
  fulfillmentMethod?: ShopFulfillmentMethod | null,
  promotions: PublicShopPromotion[] = [],
): ShopCartPricingPreview => {
  const pricingByLineId = pricingForCartLines(
    lines,
    catalogItems,
    qualifiedGiftItemIds(promotions, lines),
  );
  const merchandiseSubtotalCents = shippingSubtotal(lines, catalogItems);
  const displaySubtotalCents = lines.reduce(
    (total, line) =>
      total + (pricingByLineId.get(line.lineId)?.lineTotalCents ?? 0),
    0,
  );
  const shippingFeeCents =
    fulfillmentMethod === ShopFulfillmentMethod.PICKUP
      ? 0
      : shippingFeeFor(shipping, merchandiseSubtotalCents);

  return {
    pricingByLineId,
    merchandiseSubtotalCents,
    displaySubtotalCents,
    shippingFeeCents,
    totalCents: displaySubtotalCents + shippingFeeCents,
  };
};

// No business cap on regular items, but a technical ceiling keeps the value
// within what downstream billing amounts (int32 columns) can hold.
const TECHNICAL_MAX_QUANTITY = 100_000;

// Package checkout fulfillment creates one SoldPackage row and issues
// discounts per unit of quantity, synchronously, in a single request. Cap
// much lower than other item types so that loop stays bounded.
const PACKAGE_MAX_QUANTITY = 500;

export const quantityMaxForItemType = (itemType: ItemType): number => {
  switch (itemType) {
    case ItemType.GIFT_CARD:
      return 50;
    case ItemType.PACKAGE:
      return PACKAGE_MAX_QUANTITY;
    case ItemType.MEMBERSHIP:
      return 1;
    default:
      return TECHNICAL_MAX_QUANTITY;
  }
};

export const normalizeCartLineQuantity = (
  itemType: ItemType,
  quantity: number,
): number =>
  Math.min(quantityMaxForItemType(itemType), Math.max(1, Math.trunc(quantity)));

export const quantityMaxFor = (line: ShopCartLine): number =>
  quantityMaxForItemType(line.itemType);

/** True when shop shipping is on and the cart has an item staff flagged to ship. */
export const cartCanShip = (
  lines: ShopCartLine[],
  catalogItems: ShopItem[],
  shipping: ShopShippingPolicy | null | undefined,
): boolean => {
  if (!shipping) return false;

  const itemById = new Map(catalogItems.map((item) => [item.id, item]));
  return lines.some(
    (line) =>
      "itemId" in line && itemById.get(line.itemId)?.shipsInOnlineShop === true,
  );
};

export const cartRequiresPickupLocation = (
  lines: ShopCartLine[],
  catalogItems: ShopItem[],
): boolean => {
  const catalogRequirementById = new Map(
    catalogItems.map((item) => [item.id, item.requiresPickupLocation]),
  );

  return lines.some((line) => {
    const catalogRequirement =
      "itemId" in line ? catalogRequirementById.get(line.itemId) : undefined;
    return (
      catalogRequirement ??
      line.snapshot.requiresPickupLocation ??
      // Legacy package snapshots predate this field. If an archived package is
      // also absent from the active catalog, requiring a location is safest.
      line.itemType === ItemType.PACKAGE
    );
  });
};

/** The locations that can sell everything in the cart; null when nothing limits them. */
export const cartSoldAtLocationIds = (
  lines: ShopCartLine[],
  catalogItems: ShopItem[],
  fulfillmentMethod?: ShopFulfillmentMethod | null,
): string[] | null => {
  const itemById = new Map(catalogItems.map((item) => [item.id, item]));
  let allowed: string[] | null = null;
  for (const line of lines) {
    const item = "itemId" in line ? itemById.get(line.itemId) : undefined;
    // Shipped items leave from no clinic, so they don't narrow the pickup choice.
    if (
      fulfillmentMethod === ShopFulfillmentMethod.SHIP &&
      item?.shipsInOnlineShop
    )
      continue;
    const limitedTo = item?.soldAtLocationIds;
    if (!limitedTo?.length) continue;
    allowed = allowed
      ? allowed.filter((id) => limitedTo.includes(id))
      : [...limitedTo];
  }
  return allowed;
};

export const withLineQuantity = (
  line: ShopCartLine,
  quantity: number,
): ShopCartLine => {
  const nextQuantity = normalizeCartLineQuantity(line.itemType, quantity);

  if (line.itemType === ItemType.GIFT_CARD) {
    return {
      ...line,
      quantity: nextQuantity,
      recipients: line.recipients.slice(0, nextQuantity),
    };
  }

  if (line.itemType === ItemType.MEMBERSHIP) {
    return { ...line, quantity: 1 };
  }

  return { ...line, quantity: nextQuantity };
};
